import { Skeleton, Tag } from 'antd';
import CardLink, { Container as CardLinkContainer } from 'components/CardLink/CardLink';
import { QuantityInputContainer } from 'components/MarketItem/QuantityInput/QuantityInput';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import VisuallyHidden from 'components/VisuallyHidden';
import { getImageByCategory } from 'containers/MarketPage/ImageByCategory';
import React, { ImgHTMLAttributes, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Product, TrackingAddToCartProperty, TrackingProductImpressionProperty } from 'types/schema';
import formatMoney from 'utils/formatMoney';
import generateProductSlug from 'utils/generateProductSlug';
import generateResponsiveImage from 'utils/generateResponsiveImage';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';
import { RESPONSIVE_IMAGE_SIZES } from 'utils/urls/constants';
import Favorite, { Favorite as FavoriteContainer, FavoriteIcon } from './Favorite';
import Heart from './Heart.svg';
import { Image } from './Image';
import Details from './NewDetails';
import TagIconSvg from './NewDetails/Tag.svg';

export const Card = styled.article`
  background-color: #fcfffc;
  border: none;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Container = styled(Card)<any>`
  position: relative;
  height: 100%;
  @media (hover: hover) {
    ${QuantityInputContainer} {
      transition: all 0.2s ease-in-out;
      f &.empty {
        opacity: 1;
      }
    }

    &:hover {
      ${QuantityInputContainer} {
        &.empty {
          opacity: 1;
        }
      }
    }
  }

  ${media.md`
    width: 100%;
  `};
`;

const TagContainer = styled.div`
  position: absolute;
  bottom: 0px;
  right: 0;
  gap: 4px;
  flex-direction: column;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;

  ${media.md`
    bottom: 16px;
  `};
`;

const CustomTag = styled(Tag)`
  &.ant-tag {
    background: ${(props) => (props.bgcolor ? props.bgcolor : '#ff4852')};
    border: none;
    color: #ffffff;
    font-size: 12px;
    margin-right: 0;
    padding: 2px;
    border-radius: 4px 0px;

    &.discount-tag {
      top: 0;
      border: none;
      border-radius: 0 4px 4px 0;
      position: absolute;
      top: 0;
      left: 0;

      ${media.md`
        top: 16px;
      `};
    }

    ${media.md`
      padding: 4px;
      font-size: 14px;
      border-radius: 4px 0px 0px 4px;
    `};
  }
`;

const CardLinkContent = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.vertialOnly ? 'column' : 'row')};
  position: relative;
  height: 100%;
  // padding: 8px 8px 0;
  ${media.md`
    flex-direction: column;
  `};
`;

const ImageContainer = styled.div`
  position: relative;
  padding: ${(props) => (props.vertialOnly ? '0px' : '8px 0px 0 8px')};
  width: ${(props) => (props.vertialOnly ? '100%' : '100px')};
  ${media.md`
    width: 100%;
    padding: 0;
  `};
`;

const FrameImage = styled((props: ImgHTMLAttributes<HTMLImageElement>) => <img {...props} />)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 0px;
  pointer-events: none;
  @media (max-width: 768px) {
    ${({ isVertical }) =>
      !isVertical &&
      `
      left: 8px;
      width: 92px;
    `}
  }
`;

const OutOfStockOverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.3);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OutOfStockOverlayContent = styled.div`
  background: #000;
  opacity: 0.5;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  color: #ffffff;
`;

const BottomTagWrapper = styled.div`
  position: absolute;
  bottom: 6px;
  left: 6px;
  display: flex;
  align-items: center;
  padding: 0px 6px;
  background: #fff0e5;
  border-radius: 80px;
  gap: 2px;
  width: fit-content;
  line-height: 24px;
  color: #e56910;
  font-size: 12px;
  font-weight: 400;
  @media (max-width: 991px) {
    font-size: 8px;
    line-height: 10px;
    padding: 0px 4px;
    svg {
      width: 8px !important;
    }
  }
`;

const SavingPrice = styled.span`
  font-weight: 600;
  @media (max-width: 991px) {
    font-size: 8px;
    line-height: 12px;
  }
`;

const BookInAdvance = styled.div`
  position: absolute;
  top: 16px;
  left: 0px;
  z-index: 10;
  border-radius: 2px;
  background: #ecbe00;
  padding: 0 6px;
  display: flex;
  align-items: center;
  width: fit-content;
  @media (max-width: 991px) {
    padding: 0 4px;
  }
`;
const BookInAdvanceText = styled.div`
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  @media (max-width: 991px) {
    font-size: 10px;
    line-height: 14px;
  }
`;

type ImpressionProperty = Pick<TrackingProductImpressionProperty, 'type' | 'tag' | 'keyword'>;

type AddToCardProperty = Pick<TrackingAddToCartProperty, 'addFrom' | 'tag'>;
export interface MarketItemProps {
  product: Product;
  noImage?: boolean;
  position?: number;
  onToggleFavorite?: () => void;
  maxQuantity?: number;
  added: number;
  adding: boolean;
  loggedIn: boolean;
  loading?: boolean;
  listName?: string;
  favorite?: boolean | null;
  permissions?: string[];
  note?: string | null;
  onOpenNote?: () => void;
  onDeleteNote?: () => void;
  gotoSignIn: () => void;
  vertialOnly?: boolean;
  impressionProperties?: ImpressionProperty;
  addToCartProperties?: AddToCardProperty;
  isHideNote?: boolean;
  isProductDetail?: boolean;
  ga4Callback?: (product: Product) => void;
  isShowFrame?: boolean;
  smallPrice?: boolean;
  titleSize?: number;
  isHideQuantity?: boolean;
  hasHorizontalPadding?: boolean;
}

const addFavoritePermission = 'BUYER_FAVORITE_ITEM_ADD';

const BestSaleIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="13" viewBox="0 0 10 13" fill="none">
      <g clip-path="url(#clip0_10260_41620)">
        <path
          d="M9.79733 7.71324C9.74617 6.1352 9.19812 4.98226 8.24846 3.71372L7.34997 2.51331C7.09449 2.17174 6.90104 1.81014 6.75012 1.4171C6.60591 1.04193 6.5394 0.668611 6.53684 0.267549C6.53588 0.162428 6.46714 0.0653225 6.38848 0.0283299C6.27881 -0.0231515 6.16977 -0.00188073 6.06905 0.0711797C5.16672 0.725641 4.45496 1.5774 3.95487 2.55616C3.35118 3.73746 3.29746 4.97209 3.26709 6.26806C2.42934 6.08957 2.05588 5.15674 1.90783 4.43878C1.88833 4.34321 1.82214 4.25412 1.75403 4.22576C1.64915 4.18229 1.54364 4.20079 1.45539 4.25751C0.766326 4.70112 0.393178 5.45731 0.193654 6.21473C0.0763062 6.65926 0.036977 7.09916 0.00947854 7.53876C-0.156152 10.1837 1.86818 12.6 4.5707 12.6H5.2345C7.84814 12.387 9.88142 10.2981 9.79733 7.71324ZM5.91941 11.7069C5.3202 12.1995 4.46519 12.1943 3.87205 11.6955C3.20346 11.1335 3.13727 10.2327 3.43943 9.47409C3.64375 9.82521 3.90371 10.0872 4.33793 10.0851C4.49876 10.0842 4.61067 9.96455 4.61259 9.81288C4.62506 8.9377 4.65768 8.33564 5.26968 7.6334C5.36816 7.89327 5.48199 8.12448 5.63899 8.34674L6.09271 8.9901C6.643 9.83477 6.7725 11.0053 5.91941 11.7069Z"
          fill="#FF6E46"
        />
        <path
          d="M3.87247 11.6946C3.20388 11.1326 3.13769 10.2319 3.43985 9.4732C3.64417 9.82432 3.90445 10.0863 4.33867 10.0842C4.49918 10.0833 4.61109 9.96366 4.61333 9.81229C4.6258 8.93711 4.6581 8.33475 5.2701 7.63281C5.36858 7.89269 5.48273 8.12389 5.63941 8.34615L6.09313 8.98952C6.64342 9.83449 6.77324 11.0047 5.91983 11.7063C5.32094 12.1989 4.46593 12.1937 3.87247 11.6949V11.6946Z"
          fill="#FFCA46"
        />
      </g>
      <defs>
        <clipPath id="clip0_10260_41620">
          <rect width="9.8" height="12.6" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const MarketItem = (props: MarketItemProps) => {
  const {
    loggedIn,
    addToCartProperties,
    gotoSignIn,
    isProductDetail,
    product: {
      price,
      originalPrice,
      imageUrl,
      supplier,
      category,
      name,
      tags,
      supplierInternalCode,
      tagImageEn,
      tagImageVn,
      priceV2,
      savingAmount,
    },
    ga4Callback,
    isShowFrame = true,
  } = props;

  const responsiveImageUrl = useMemo(() => {
    return imageUrl
      ? generateResponsiveImage(imageUrl, RESPONSIVE_IMAGE_SIZES.SMALL, RESPONSIVE_IMAGE_SIZES.SMALL)
      : imageUrl;
  }, [imageUrl]);
  const lang = localStorage.getItem('lang') || 'vi';
  const image = category && !imageUrl ? getImageByCategory(category) : responsiveImageUrl;
  const frameTagImage = lang === 'vi' ? tagImageVn : tagImageEn || '';
  const favorite = props.favorite ? 'true' : 'false';
  const supplierId = supplier && supplier.id;
  const supplierName = supplier && supplier.name;
  const itemDiscountExists = originalPrice > price;
  const detailProps = {
    ...props,
    ...addToCartProperties,
    loggedIn: loggedIn,
    isProductDetail: isProductDetail,
    product: {
      ...props.product,
      image: image,
      favorite: favorite,
      supplierId: supplierId,
      supplierName: supplierName,
      note: props.note,
    },
    itemDiscountExists,
  };

  const onClickToggleFavorite = () => {
    props.onToggleFavorite?.();
  };

  return (
    <Container title={name} bordered={props?.vertialOnly}>
      {props.loading ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {!props.noImage && (
            <Skeleton className="card-skeleton" title={false} active avatar={{ shape: 'square' }} paragraph={false} />
          )}
          <Skeleton title={false} paragraph={{ rows: 4, width: '100%' }} active />
        </div>
      ) : (
        <CardLink>
          <CardLinkContent vertialOnly={props.vertialOnly}>
            {!loggedIn && (
              <FavoriteContainer type="secondary" shape="circle" onClick={gotoSignIn} isProductDetail={false}>
                <FavoriteIcon renderAsImg={false} src={Heart} aria-label="Add to favorite" />
              </FavoriteContainer>
            )}
            {props?.permissions && props?.permissions?.indexOf?.(addFavoritePermission) > -1 && (
              <Favorite onClick={onClickToggleFavorite} favorite={props.favorite} />
            )}
            <div>
              <Link
                to={`/products/${generateProductSlug(name, supplierInternalCode, lang)}`}
                onClick={() => {
                  if (ga4Callback) {
                    ga4Callback(props.product);
                  }
                }}
              >
                <ImageContainer style={{ position: 'relative' }} vertialOnly={props.vertialOnly}>
                  {props?.product?.tags.includes('BOOK_IN_ADVANCE') && (
                    <BookInAdvance>
                      <BookInAdvanceText>
                        <FormattedMessage {...utilsMessages.bookInAdvanceTag} />
                      </BookInAdvanceText>
                    </BookInAdvance>
                  )}
                  {!props.noImage && image && <Image zoomIn src={image} alt={`${props.product.name} frame`} />}
                  {frameTagImage && isShowFrame && <FrameImage src={frameTagImage} isVertical={props.vertialOnly} />}
                  {!props.product.inStock && (
                    <OutOfStockOverlayContainer>
                      <OutOfStockOverlayContent>
                        <FormattedMessage {...utilsMessages.outStock} />
                      </OutOfStockOverlayContent>
                    </OutOfStockOverlayContainer>
                  )}
                  <VisuallyHidden asComponent="span">{props.product.name}</VisuallyHidden>
                  {!!Number(savingAmount) && (
                    <BottomTagWrapper>
                      <SVGIcon src={TagIconSvg} renderAsImg={false} />
                      <span style={{ flexShrink: 0 }}>
                        <FormattedMessage {...utilsMessages.savingAmount} />
                      </span>
                      <SavingPrice>{formatMoney(savingAmount || 0)}đ</SavingPrice>
                    </BottomTagWrapper>
                  )}
                  {!Number(savingAmount) && tags.includes('BEST_SELLER') && (
                    <BottomTagWrapper>
                      <BestSaleIcon />
                      <span style={{ flexShrink: 0, flexGrow: 0 }}>
                        <FormattedMessage {...utilsMessages.bestSeller} />
                      </span>
                    </BottomTagWrapper>
                  )}
                </ImageContainer>
              </Link>
            </div>
            <div style={{ flex: 1 }}>
              <Details {...detailProps} />
            </div>
          </CardLinkContent>
        </CardLink>
      )}
    </Container>
  );
};

const areEqual = (prevProps: MarketItemProps, nextProps: MarketItemProps) => {
  return (
    prevProps.product.id === nextProps.product.id &&
    prevProps.favorite === nextProps.favorite &&
    prevProps.loading === nextProps.loading &&
    prevProps.added === nextProps.added &&
    prevProps.adding === nextProps.adding &&
    prevProps.note === nextProps.note &&
    prevProps?.permissions?.length === nextProps?.permissions?.length
  );
};

export default React.memo(MarketItem, areEqual);
